import { UnknownQuestionType } from 'tabs/nlp/components/unansweredQuestions/types'
import { toRgba } from 'tabs/topQuestions/utils/ColorUtils'
import { formatDateObject, toDateObjectFromTimestamp } from 'tabs/topQuestions/utils/DateUtils'
import { SelectedQuestionType } from 'tabs/topQuestions/lib/top_questions_search/topQuestionsSearchTypes'
import { ClusteringExecutionData, ColorRgb, ExecutionClusters } from 'tabs/topQuestions/lib/question_clustering_status/questionClusteringStatusTypes'
import { ChartData } from 'chart.js'
import { ChartDataset } from 'chart.js/dist/types'
import { range } from 'lodash'

function getCountFun(type: SelectedQuestionType): (clusterDateInfo: ExecutionClusters) => number {
  switch (type) {
    case UnknownQuestionType.bcp:
    case UnknownQuestionType.beach:
      return (clusterDateInfo: ExecutionClusters) => clusterDateInfo.daxCount
    case UnknownQuestionType.faq:
      return (clusterDateInfo: ExecutionClusters) => clusterDateInfo.faqCount
    case UnknownQuestionType.unknown:
      return (clusterDateInfo: ExecutionClusters) => clusterDateInfo.unknownCount
    default:
      return (clusterDateInfo: ExecutionClusters) => clusterDateInfo.questionsCount
  }
}

export const convertQuestionDataToChart = (data: ClusteringExecutionData[],
                                           colorMap: Record<string, number[]>,
                                           clustersNamesMap: Record<string, string>,
                                           questionType: SelectedQuestionType,
                                           hoveredClusterId: string): ChartData => {

  return {
    // dates of the clustering information
    labels: evaluateLabels(data),
    datasets: evaluateDatasets(questionType, colorMap, clustersNamesMap, data, hoveredClusterId),
  }
}

function evaluateLabels(data: ClusteringExecutionData[]) {
  return data[0].executionClusters.map(clusterInfo => clusterInfo.endPeriod)
    .map(toDateObjectFromTimestamp).map(formatDateObject)
}


function evaluateDatasets(questionType: SelectedQuestionType,
                          colorMap: Record<string, number[]>,
                          clustersNamesMap: Record<string, string>,
                          data: ClusteringExecutionData[],
                          hoveredClusterId: string): ChartDataset[] {

  const getCount = getCountFun(questionType)
  const maxDaysInCluster = Math.max(...data.map(val => val.executionClusters.length))
  return data
    .filter(clusterInfo => {
      if (hoveredClusterId == undefined || hoveredClusterId == '') {
        return true
      } else {
        return hoveredClusterId == clusterInfo.clusterId
      }
    })
    .map((clusterInfo, idx) => {
      const executionClusters = clusterInfo.executionClusters
      const clusterId = clusterInfo.clusterId
      const color = colorMap[clusterId]
      const clusterData = executionClusters.map(clusterDateInfo => getCount(clusterDateInfo))
      let data
      if (maxDaysInCluster - executionClusters.length > 0) {
        const frontPadding = range(0, maxDaysInCluster - executionClusters.length).map(i => 0)
        data = [...frontPadding, ...clusterData]
      } else {
        data = clusterData
      }
      return {
        label: clustersNamesMap[clusterId] || '',
        data: data,
        borderColor: getColor(color, getBorderAlpha(clusterId, hoveredClusterId)),
        backgroundColor: getColor(color, getBackgroundAlpha(clusterId, hoveredClusterId)),
        fill: true,
        tension: 0.4,
      }
    })
}

export function getBorderAlpha(clusterId: string, hoveredClusterId: string) {
  if (hoveredClusterId == undefined || hoveredClusterId == '') {
    return 1.0
  }
  return clusterId === hoveredClusterId ? 1.0 : 0.1
}

export function getBackgroundAlpha(clusterId: string, hoveredClusterId: string) {
  if (hoveredClusterId == undefined || hoveredClusterId == '') {
    return 0.6
  }
  return clusterId === hoveredClusterId ? 0.6 : 0.05
}

export function getTooltipAlpha(clusterId: string, hoveredClusterId: string) {
  if (hoveredClusterId == undefined || hoveredClusterId == '') {
    return 0.6
  }
  return clusterId === hoveredClusterId ? 1.0 : 0.8
}

export function getColor(color: number[], alpha: number) {
  return color != undefined ? toRgba(...color as ColorRgb, alpha) : undefined
}
